import React, { useState, useEffect, useMemo } from 'react';
import { millInventoryAPI } from '../../services/millAPI';
import MobileScrollTable from '../../components/common/MobileScrollTable';
import './Mill.css';

interface RawMaterial {
  id: number;
  name: string;
  sku?: string;
  category: string;
  unit: string;
  unit_of_measure?: string;  // API may return this instead of unit
  quantity_on_hand: number;
  available_quantity?: number;
  reorder_level: number;
  cost_per_unit?: number;
  stock_status?: 'ok' | 'low' | 'out';
}

const MillInventory: React.FC = () => {
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'quantity' | 'status'>('name');
  const [filterStatus, setFilterStatus] = useState<'all' | 'ok' | 'low' | 'out'>('all');
  const [successMessage, setSuccessMessage] = useState('');

  // Update quantity modal
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<RawMaterial | null>(null);
  const [adjustmentQty, setAdjustmentQty] = useState('');
  const [adjustmentType, setAdjustmentType] = useState('PURCHASE');
  const [adjustmentNotes, setAdjustmentNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadMaterials();
  }, []);

  // Helper to safely extract array from API response
  const extractArray = (data: any): RawMaterial[] => {
    if (Array.isArray(data)) return data;
    if (data?.results && Array.isArray(data.results)) return data.results;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.items && Array.isArray(data.items)) return data.items;
    if (data?.materials && Array.isArray(data.materials)) return data.materials;
    return [];
  };

  // Helper to get the unit with fallback
  const getUnit = (m: RawMaterial) => m.unit || m.unit_of_measure || 'lbs';

  // Helper to safely format numbers
  const safeNumber = (val: number | null | undefined) => (val ?? 0).toLocaleString();

  const loadMaterials = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await millInventoryAPI.getAll();
      const materialsData = extractArray(response.data);
      setMaterials(materialsData);
    } catch (err: any) {
      setError('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleOpenUpdateModal = (material: RawMaterial) => {
    setSelectedMaterial(material);
    setAdjustmentQty('');
    setAdjustmentType('PURCHASE');
    setAdjustmentNotes('');
    setShowUpdateModal(true);
  };

  const handleUpdateQuantity = async () => {
    if (!selectedMaterial || !adjustmentQty) return;

    const qty = parseFloat(adjustmentQty);
    if (isNaN(qty)) {
      alert('Please enter a valid quantity');
      return;
    }

    try {
      setUpdating(true);
      const response = await millInventoryAPI.adjust(selectedMaterial.id, {
        quantity: qty,
        transaction_type: adjustmentType,
        notes: adjustmentNotes,
      });

      // Update local state
      setMaterials(prev => prev.map(m =>
        m.id === selectedMaterial.id
          ? {
              ...m,
              quantity_on_hand: response.data.new_quantity,
              available_quantity: response.data.new_quantity,
              stock_status: response.data.new_quantity <= 0 ? 'out' :
                           response.data.new_quantity <= m.reorder_level ? 'low' : 'ok',
            }
          : m
      ));

      showSuccess(`Updated ${selectedMaterial.name}: ${qty > 0 ? '+' : ''}${qty.toLocaleString()} ${selectedMaterial.unit}`);
      setShowUpdateModal(false);
      setSelectedMaterial(null);
    } catch (err: any) {
      console.error('Error updating inventory:', err);
      alert('Failed to update: ' + (err.response?.data?.error || err.message));
    } finally {
      setUpdating(false);
    }
  };

  const filteredAndSortedMaterials = useMemo(() => {
    let result = [...materials];

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(m =>
        (m.name || '').toLowerCase().includes(search) ||
        (m.sku || '').toLowerCase().includes(search) ||
        (m.category || '').toLowerCase().includes(search)
      );
    }

    // Filter by status (handle case-insensitive and undefined)
    if (filterStatus !== 'all') {
      result = result.filter(m => {
        const status = (m.stock_status || 'ok').toLowerCase();
        return status === filterStatus.toLowerCase();
      });
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'quantity':
          return (b.quantity_on_hand || 0) - (a.quantity_on_hand || 0);
        case 'status':
          const statusOrder: { [key: string]: number } = { out: 0, low: 1, ok: 2 };
          const aStatus = (a.stock_status || 'ok').toLowerCase();
          const bStatus = (b.stock_status || 'ok').toLowerCase();
          return (statusOrder[aStatus] ?? 2) - (statusOrder[bStatus] ?? 2);
        case 'name':
        default:
          return (a.name || '').localeCompare(b.name || '');
      }
    });

    return result;
  }, [materials, searchTerm, sortBy, filterStatus]);

  const getStockStatusClass = (status: string) => {
    switch (status) {
      case 'ok':
        return 'stock-ok';
      case 'low':
        return 'stock-low';
      case 'out':
        return 'stock-out';
      default:
        return '';
    }
  };

  const getStockStatusLabel = (status: string) => {
    switch (status) {
      case 'ok':
        return 'In Stock';
      case 'low':
        return 'Low Stock';
      case 'out':
        return 'Out of Stock';
      default:
        return status;
    }
  };

  // Summary stats
  const stats = useMemo(() => {
    const total = materials.length;
    const lowStock = materials.filter(m => (m.stock_status || '').toLowerCase() === 'low').length;
    const outOfStock = materials.filter(m => (m.stock_status || '').toLowerCase() === 'out').length;
    return { total, lowStock, outOfStock };
  }, [materials]);

  if (loading) {
    return (
      <div className="mill-page">
        <div className="mill-loading">
          <div className="mill-loading-spinner"></div>
          <p>Loading inventory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mill-page">
      {/* Success Toast */}
      {successMessage && (
        <div className="mill-toast mill-toast-success">
          <span className="toast-icon">✓</span>
          {successMessage}
        </div>
      )}

      <div className="mill-page-header">
        <h1>Raw Material Inventory</h1>
        <p className="mill-page-subtitle">Track and update ingredient stock levels</p>
      </div>

      {error && <div className="mill-alert mill-alert-error">{error}</div>}

      {/* Summary Stats */}
      <div className="inventory-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Items</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-value">{stats.lowStock}</div>
          <div className="stat-label">Low Stock</div>
        </div>
        <div className="stat-card danger">
          <div className="stat-value">{stats.outOfStock}</div>
          <div className="stat-label">Out of Stock</div>
        </div>
      </div>

      {/* Filters */}
      <div className="inventory-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search ingredients..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value as any)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="ok">In Stock</option>
            <option value="low">Low Stock</option>
            <option value="out">Out of Stock</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Sort by:</label>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as any)}
            className="filter-select"
          >
            <option value="name">Name</option>
            <option value="quantity">Quantity</option>
            <option value="status">Status</option>
          </select>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="inventory-table-container">
        <MobileScrollTable>
        <table className="mill-table inventory-table">
          <thead>
            <tr>
              <th>Ingredient</th>
              <th>Category</th>
              <th>Quantity</th>
              <th>Reorder Level</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedMaterials.map(material => (
              <tr key={material.id} className={getStockStatusClass(material.stock_status || 'ok')}>
                <td className="ingredient-cell">
                  <div className="ingredient-name">{material.name || 'Unknown'}</div>
                  {material.sku && <div className="ingredient-sku">SKU: {material.sku}</div>}
                </td>
                <td className="category-cell">{material.category || '-'}</td>
                <td className="quantity-cell">
                  <span className="quantity-value">
                    {safeNumber(material.quantity_on_hand)}
                  </span>
                  <span className="quantity-unit">{getUnit(material)}</span>
                </td>
                <td className="reorder-cell">
                  {safeNumber(material.reorder_level)} {getUnit(material)}
                </td>
                <td className="status-cell">
                  <span className={`status-badge ${getStockStatusClass(material.stock_status || 'ok')}`}>
                    {getStockStatusLabel(material.stock_status || 'ok')}
                  </span>
                </td>
                <td className="actions-cell">
                  <button
                    className="mill-btn mill-btn-sm mill-btn-primary"
                    onClick={() => handleOpenUpdateModal(material)}
                  >
                    Update Qty
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </MobileScrollTable>

        {filteredAndSortedMaterials.length === 0 && (
          <div className="no-results">
            <p>No materials found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Update Quantity Modal */}
      {showUpdateModal && selectedMaterial && (
        <div className="mill-modal-overlay" onClick={() => setShowUpdateModal(false)}>
          <div className="mill-modal" onClick={e => e.stopPropagation()}>
            <div className="mill-modal-header">
              <h3>Update Quantity</h3>
              <button className="modal-close" onClick={() => setShowUpdateModal(false)}>×</button>
            </div>
            <div className="mill-modal-body">
              <div className="material-info">
                <h4>{selectedMaterial.name || 'Unknown'}</h4>
                <p>Current: <strong>{safeNumber(selectedMaterial.quantity_on_hand)} {getUnit(selectedMaterial)}</strong></p>
              </div>

              <div className="form-group">
                <label>Transaction Type</label>
                <select
                  className="form-select"
                  value={adjustmentType}
                  onChange={e => setAdjustmentType(e.target.value)}
                >
                  <option value="PURCHASE">Purchase / Receiving</option>
                  <option value="ADJUSTMENT">Inventory Adjustment</option>
                  <option value="DAMAGE">Damage / Waste</option>
                  <option value="RETURN">Return to Supplier</option>
                </select>
              </div>

              <div className="form-group">
                <label>Quantity to Add/Remove ({getUnit(selectedMaterial)})</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-input"
                  value={adjustmentQty}
                  onChange={e => setAdjustmentQty(e.target.value)}
                  onBlur={e => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val)) setAdjustmentQty(String(val));
                    else setAdjustmentQty('');
                  }}
                  placeholder="Enter amount (negative to remove)"
                />
                <div className="help-text">
                  Use positive numbers to add, negative to remove
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  className="form-textarea"
                  value={adjustmentNotes}
                  onChange={e => setAdjustmentNotes(e.target.value)}
                  placeholder="Reason for adjustment..."
                  rows={2}
                />
              </div>

              {adjustmentQty && !isNaN(parseFloat(adjustmentQty)) && (
                <div className="preview-result">
                  <strong>New Quantity:</strong>{' '}
                  {((selectedMaterial.quantity_on_hand ?? 0) + parseFloat(adjustmentQty)).toLocaleString()} {getUnit(selectedMaterial)}
                </div>
              )}
            </div>
            <div className="mill-modal-footer">
              <button
                className="mill-btn mill-btn-secondary"
                onClick={() => setShowUpdateModal(false)}
              >
                Cancel
              </button>
              <button
                className="mill-btn mill-btn-primary"
                onClick={handleUpdateQuantity}
                disabled={updating || !adjustmentQty}
              >
                {updating ? 'Updating...' : 'Update Quantity'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MillInventory;
